import React, { useCallback, useEffect, useRef, useState } from 'react';
import ChessBoardView from '@/components/chess/ChessBoardView';
import { Chess } from 'chess.js';
import { api } from '@/lib/api';
import { createTree, addMove, getNode, getChildren, getParent, navigateTo, getPath } from '@/lib/moveTree';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X, ShieldCheck, Loader2, FlaskConical } from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════
// PuzzleReplay — Stage 3.2.2 · Fase C
// Reproductor READ-ONLY del árbol OFICIAL de solución de un ejercicio ya
// finalizado (solved/locked). NO analiza, NO mueve piezas libremente, NO crea
// variantes, NO envía mutaciones al backend. Su ÚNICA request es un GET
// on-demand a /classrooms/{roomId}/puzzle/solution-tree. El backend es la
// fuente de verdad y autoriza server-side; aquí sólo navegamos localmente el
// árbol devuelto, hidratándolo en el moveTree existente (sin modificarlo).
// ═══════════════════════════════════════════════════════════════════════════

const ERR_MSG = {
  e409: 'La solución estará disponible al finalizar el ejercicio.',
  e403: 'No tienes permiso para reproducir esta solución.',
  e404: 'La solución ya no está disponible.',
  error: 'No fue posible cargar la solución.',
};

// Hidrata el árbol del backend en el moveTree local existente. Marca los nodos
// oficiales en un Set (mismo patrón que ClassroomLive). Devuelve currentId=root.
function hydrate(data) {
  let t = createTree(data?.root_fen || undefined);
  const official = new Set();
  const seed = (tt, parentId, nodes) => {
    for (const n of (nodes || [])) {
      const t2 = addMove(tt, parentId, {
        san: n.san, fen: n.fen_after, from: n.from, to: n.to,
        ...(n.promotion ? { promotion: n.promotion } : {}),
      });
      const childId = t2.currentId;   // addMove fija currentId al hijo creado
      official.add(childId);
      tt = seed(t2, childId, n.children || []);
    }
    return tt;
  };
  t = seed(t, t.rootId, Array.isArray(data?.children) ? data.children : []);
  t = navigateTo(t, t.rootId);
  return { tree: t, official };
}

export default function PuzzleReplay({ roomId, activePuzzleId, onClose }) {
  const [status, setStatus] = useState('loading'); // loading|ready|e409|e403|e404|error
  const [tree, setTree] = useState(null);
  const officialRef = useRef(new Set());
  // L2 — IDs de nodos creados por el alumno (análisis local). Un nodo es
  // OFICIAL si officialRef lo contiene; en caso contrario es LOCAL. Temporal:
  // vive solo mientras el componente esté montado (se pierde al cerrar/reabrir).
  const localNodeIds = useRef(new Set());
  // L1 — modo análisis. Arranca SIEMPRE en false (tablero read-only).
  const [analysisMode, setAnalysisMode] = useState(false);
  const [boardW, setBoardW] = useState(320);
  const roRef = useRef(null);

  const boardWrapRef = useCallback((node) => {
    if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
    if (node) {
      const measure = () => setBoardW(Math.max(240, Math.min(440, node.clientWidth)));
      measure();
      roRef.current = new ResizeObserver(measure);
      roRef.current.observe(node);
    }
  }, []);

  // On-demand fetch. Keyed por activePuzzleId: si cambia el ejercicio, se
  // vuelve a solicitar (el padre además desmonta el Replay). Una sola request.
  useEffect(() => {
    let cancel = false;
    setStatus('loading'); setTree(null);
    // Reset de análisis local en cada carga (nuevo ejercicio / reapertura).
    setAnalysisMode(false); localNodeIds.current = new Set();
    api.get(`/classrooms/${roomId}/puzzle/solution-tree`)
      .then(({ data }) => {
        if (cancel) return;
        const { tree: t, official } = hydrate(data);
        officialRef.current = official;
        setTree(t);
        setStatus('ready');
      })
      .catch((err) => {
        if (cancel) return;
        const s = err?.response?.status;
        setStatus(s === 409 ? 'e409' : s === 403 ? 'e403' : s === 404 ? 'e404' : 'error');
      });
    return () => { cancel = true; };
  }, [roomId, activePuzzleId]);

  const node = tree ? getNode(tree, tree.currentId) : null;
  const fen = node?.move?.fen || node?.fen || null;
  const children = tree ? getChildren(tree, tree.currentId) : [];
  const parent = tree ? getParent(tree, tree.currentId) : null;
  const rootNode = tree ? getNode(tree, tree.rootId) : null;
  const rootFen = rootNode?.fen || fen;
  const orientation = (rootFen && rootFen.split(' ')[1] === 'b') ? 'black' : 'white';
  const path = tree ? getPath(tree, tree.currentId).filter(n => n.move) : [];

  const goNext = () => { if (children[0]) setTree(navigateTo(tree, children[0].id)); };
  const goPrev = () => { if (parent) setTree(navigateTo(tree, parent.id)); };
  const goTo = (id) => setTree(navigateTo(tree, id));

  // L2 — jugada de análisis LOCAL. Valida con chess.js sobre el FEN del nodo
  // actual y crea una rama con addMove (aditivo/inmutable → nunca altera nodos
  // oficiales). NO envía ninguna request. Respeta la deduplicación de addMove:
  // si la jugada coincide con un hijo existente, addMove reutiliza ese nodo; y
  // si ese nodo ya es oficial, NO se reclasifica como local.
  const onPieceDrop = useCallback((source, target) => {
    if (!analysisMode || !tree || !fen) return false;
    try {
      const c = new Chess(fen);
      const mv = c.move({ from: source, to: target, promotion: 'q' });
      if (!mv) return false;
      const t2 = addMove(tree, tree.currentId, {
        san: mv.san, fen: c.fen(), from: mv.from, to: mv.to,
        ...(mv.promotion ? { promotion: mv.promotion } : {}),
      });
      const newId = t2.currentId;
      if (!officialRef.current.has(newId)) localNodeIds.current.add(newId);
      setTree(t2); // navigateTo implícito: addMove deja currentId en el nodo destino
      return true;
    } catch (_) {
      return false; // jugada ilegal → snap-back, sin efectos
    }
  }, [analysisMode, tree, fen]);

  return (
    <div data-testid="replay-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-3">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
          <div>
            <div className="font-display font-bold leading-tight">Reproducir solución</div>
            <div className="text-[11px] inline-flex items-center gap-1 opacity-90"><ShieldCheck className="w-3 h-3"/> Solución oficial</div>
          </div>
          <button data-testid="replay-close-btn" onClick={onClose} className="rounded-full p-1.5 hover:bg-white/20 transition-colors"><X className="w-5 h-5"/></button>
        </div>

        <div className="p-4">
          {status === 'loading' && (
            <div data-testid="replay-status" className="py-10 text-center text-slate-500 flex flex-col items-center gap-2">
              <Loader2 className="w-6 h-6 animate-spin"/> Preparando solución...
            </div>
          )}

          {['e409', 'e403', 'e404', 'error'].includes(status) && (
            <div data-testid="replay-status" className="py-10 text-center text-slate-600 flex flex-col items-center gap-3">
              <p className="text-sm">{ERR_MSG[status]}</p>
              <Button data-testid="replay-error-close-btn" size="sm" variant="outline" className="rounded-full" onClick={onClose}>Cerrar</Button>
            </div>
          )}

          {status === 'ready' && tree && fen && (
            <div className="flex flex-col items-center gap-3">
              <div ref={boardWrapRef} className="w-full flex justify-center">
                <ChessBoardView fen={fen} boardWidth={boardW} orientation={orientation} arePiecesDraggable={analysisMode} onPieceDrop={analysisMode ? onPieceDrop : undefined} />
              </div>

              {/* L3 · breadcrumb con distinción oficial (gris) vs análisis local
                  (ámbar/itálica). El nodo actual (último del path) se resalta.
                  Reutiliza officialRef + tree.currentId; sin estado nuevo. */}
              <div data-testid="replay-line" className="w-full text-xs text-center min-h-[1rem] font-mono flex flex-wrap justify-center gap-x-1">
                {path.length === 0 ? (
                  <span className="text-slate-500">Posición inicial</span>
                ) : path.map((n, idx) => {
                  const local = !officialRef.current.has(n.id);
                  const active = idx === path.length - 1;
                  return (
                    <span key={n.id}
                      data-testid={local ? 'replay-line-local' : 'replay-line-official'}
                      className={`${local ? 'text-amber-700 italic' : 'text-slate-500'} ${active ? 'font-bold underline underline-offset-2' : ''}`}>
                      {n.move.san}
                    </span>
                  );
                })}
              </div>

              <div className="flex items-center justify-center gap-2 w-full">
                <Button data-testid="replay-prev-btn" size="sm" variant="outline" className="rounded-full" disabled={!parent} onClick={goPrev}>
                  <ChevronLeft className="w-4 h-4 mr-1"/> Anterior
                </Button>
                <Button data-testid="replay-next-btn" size="sm" className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white" disabled={children.length === 0} onClick={goNext}>
                  Siguiente <ChevronRight className="w-4 h-4 ml-1"/>
                </Button>
              </div>

              <div className="flex justify-center w-full">
                {!analysisMode ? (
                  <Button data-testid="replay-analyze-btn" size="sm" variant="outline" className="rounded-full" onClick={() => setAnalysisMode(true)}>
                    Analizar posición
                  </Button>
                ) : (
                  <Button data-testid="replay-exit-analysis-btn" size="sm" variant="outline" className="rounded-full border-amber-400 text-amber-700" onClick={() => setAnalysisMode(false)}>
                    Salir de análisis
                  </Button>
                )}
              </div>

              {children.length > 0 && (
                <div className="w-full">
                  <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-1 text-center">
                    {children.length > 1 ? 'Variantes' : 'Continuación'}
                  </div>
                  {/* L3 · chips diferenciados: oficial (indigo mainline / slate
                      hermano) vs análisis local (ámbar punteado + icono). Clasif.
                      con officialRef; sin estado nuevo. */}
                  <div className="flex flex-wrap justify-center gap-1.5">
                    {children.map((c, i) => {
                      const local = !officialRef.current.has(c.id);
                      const isMain = i === 0 && !local;
                      return (
                        <button key={c.id}
                          data-testid={local ? `replay-variant-chip-local-${i}` : `replay-variant-chip-${i}`}
                          onClick={() => goTo(c.id)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-mono border transition-colors ${
                            local
                              ? 'border-dashed border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100'
                              : isMain
                                ? 'border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                          }`}>
                          {local && <FlaskConical className="w-3 h-3"/>}
                          {c.move.san}{isMain && children.length > 1 ? ' ★' : ''}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* L3 · leyenda discreta: sólo cuando hay análisis local activo o
                  ya existen nodos locales creados. Reutiliza analysisMode +
                  localNodeIds; sin llamadas backend. */}
              {(analysisMode || localNodeIds.current.size > 0) && (
                <div data-testid="replay-legend" className="w-full flex items-center justify-center gap-4 text-[10px] text-slate-400">
                  <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-indigo-500"/> Oficial</span>
                  <span className="inline-flex items-center gap-1"><FlaskConical className="w-3 h-3 text-amber-500"/> Análisis local</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
